import { enqueueSnackbar } from 'notistack';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { axFetch } from '/@/store/session/actions';
import type { CropMonitoring, CropType } from '/@/types/crop-monitoring';
import { invalidate } from '/@/utils/swr';

// Types based on API schema
export type VisitReportStatus = 'draft' | 'saved';

export type SectionId =
    | 'texts-and-images'
    | 'gdu-evolution'
    | 'precipitations-history'
    | 'temperatures-trend'
    | 'pines-registered-in-fieldview';

export interface FieldObservationWrite {
    id?: number;
    field: number;
    campaign?: number | null;
    cropMonitoring?: number | null;
    plantingDate?: string | null;
    productName?: string;
    vegetativeStage?: string;
    content?: string;
    scoutingObservations?: number[];
    sections?: SectionId[];
    order?: number;
}

export interface ScoutingObservationData {
    id: number;
    [key: string]: unknown;
}

export interface FieldObservation extends Omit<FieldObservationWrite, 'cropMonitoring'> {
    id: number;
    visitReport: number;
    fieldName: string;
    campaign: number | null;
    cropMonitoring: CropMonitoring | null;
    scoutingObservationsData: ScoutingObservationData[];
    sections: SectionId[];
    order: number;
    created: string;
    modified: string;
    crop: CropType;
}

export interface VisitReportWrite {
    id?: number;
    status?: VisitReportStatus;
    date: string;
    participants?: string;
    userNode?: number | null;
    observation?: string;
    fieldObservations?: FieldObservationWrite[];
}

export interface VisitReport {
    id: number;
    status?: VisitReportStatus;
    date: string;
    participants?: string;
    userNode: number;
    observation?: string;
    fieldObservations: FieldObservation[];
    created: string;
    modified: string;
}

export interface VisitReportList {
    id: number;
    status?: VisitReportStatus;
    date: string;
    participants?: string;
    userNode: number;
    observation?: string;
    fieldObservationsCount: string;
    created: string;
    modified: string;
    createdBy: number;
}

export interface PaginatedVisitReportList {
    count: number;
    next: string | null;
    previous: string | null;
    results: VisitReportList[];
}

export interface VisitReportsListParams {
    fieldId?: number;
    campaign?: number | null;
    status?: VisitReportStatus | null;
    page?: number;
    page_size?: number;
    ordering?: string;
    search?: string;
    created_by?: number;
    created_by__in?: number[];
    updated_by?: number;
    user_node?: number;
    date?: string;
}

// API Functions
const getVisitReports = async (params: VisitReportsListParams & { key: string }) => {
    const { ...queryParams } = params;
    const searchParams = new URLSearchParams();

    if (queryParams.page) searchParams.append('page', String(queryParams.page));
    if (queryParams.page_size) searchParams.append('page_size', String(queryParams.page_size));
    if (queryParams.ordering) searchParams.append('ordering', queryParams.ordering);
    if (queryParams.search) searchParams.append('search', queryParams.search);
    if (queryParams.fieldId) searchParams.append('field', String(queryParams.fieldId));
    if (queryParams.created_by) searchParams.append('created_by', String(queryParams.created_by));
    if (queryParams.created_by__in && Array.isArray(queryParams.created_by__in)) {
        searchParams.append('created_by__in', queryParams.created_by__in.join(','));
    }
    if (queryParams.updated_by) searchParams.append('updated_by', String(queryParams.updated_by));
    if (queryParams.user_node) searchParams.append('user_node', String(queryParams.user_node));
    if (queryParams.campaign) searchParams.append('campaign', String(queryParams.campaign));
    if (queryParams.status) searchParams.append('status', queryParams.status);
    if (queryParams.date) searchParams.append('date', queryParams.date);

    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/fields/visit-reports/?${searchParams.toString()}`,
        window.location.origin
    );

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

const getVisitReportById = async ({ id }: { id: number; key: string }) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/fields/visit-reports/${id}/`, window.location.origin);

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

const createVisitReport = async (data: VisitReportWrite) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/fields/visit-reports/`, window.location.origin);

    const res = await axFetch.post(apiUrl.toString(), data);
    return res.data;
};

const updateVisitReport = async ({ id, data }: { id: number; data: Partial<VisitReportWrite> }) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/fields/visit-reports/${id}/`, window.location.origin);

    const res = await axFetch.patch(apiUrl.toString(), data);
    return res.data;
};

// Hooks
export const useVisitReports = (
    params: VisitReportsListParams = {},
    options: { enabled?: boolean } = { enabled: true }
) => {
    const key = options.enabled
        ? {
              key: 'useVisitReports',
              ...params,
          }
        : null;

    return useSWR<PaginatedVisitReportList>(key, getVisitReports, {
        revalidateIfStale: true,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        keepPreviousData: true,
    });
};

export const useVisitReportById = (id: number | undefined, options: { enabled?: boolean } = { enabled: true }) => {
    const key = options.enabled && id ? { key: 'useVisitReportById', id } : null;

    return useSWR<VisitReport>(key, getVisitReportById, {
        revalidateIfStale: true,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        keepPreviousData: true,
    });
};

export const useCreateVisitReport = () => {
    return useSWRMutation(
        { key: 'useCreateVisitReport' },
        (_, { arg }: { arg: VisitReportWrite }) => createVisitReport(arg),
        {
            onSuccess: () => {
                revalidateVisitReports();
            },
        }
    );
};

export const useUpdateVisitReport = (id: number) => {
    return useSWRMutation(
        { key: 'useUpdateVisitReport', id },
        (_, { arg }: { arg: Partial<VisitReportWrite> }) => updateVisitReport({ id, data: arg }),
        {
            onSuccess: () => {
                revalidateVisitReports(id);
            },
        }
    );
};

export const revalidateVisitReports = (id?: number) => {
    if (id) {
        invalidate({ key: 'useVisitReportById', id });
    }
    invalidate({ key: 'useVisitReports' });
};

// Field Observation Image Types
export interface FieldObservationImage {
    id: number;
    image: string;
    url: string;
    caption: string;
    order: number;
    userNode: number;
    created: string;
    modified: string;
}

// Upload field observation image
export const uploadFieldObservationImage = async (file: File): Promise<FieldObservationImage> => {
    const formData = new FormData();
    formData.append('image', file);

    const apiUrl = new URL(
        `${(settings as any).urls.apiDomain}/core/v1/fields/field-observation-images/`,
        window.location.origin
    );

    const res = await axFetch.post(apiUrl.toString(), formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });

    return res.data;
};

export const useDeleteVisitReport = ({ visitReportId }: { visitReportId: number }) => {
    return useSWRMutation({ key: 'useDeleteVisitReport', visitReportId }, () => deleteVisitReport(visitReportId), {
        onSuccess: () => {
            revalidateVisitReports(visitReportId);
            enqueueSnackbar(_('Informe de visita eliminado correctamente'), {
                variant: 'success',
            });
        },
    });
};

const deleteVisitReport = async (visitReportId: number) => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/fields/visit-reports/${visitReportId}/`,
        window.location.origin
    );
    const res = await axFetch.delete(apiUrl.toString());
    return res.data;
};
